/** Display helpers shared by the site and the services. Presentation only. */
const ETH = 10n ** 18n;
/** Fixed-point ETH with a sensible number of places for the magnitude. */
export function formatEth(wei, opts = {}) {
    const { symbol = false } = opts;
    const neg = wei < 0n;
    const v = neg ? -wei : wei;
    const places = opts.places ?? (v === 0n ? 2 : v >= ETH ? 3 : v >= ETH / 1000n ? 4 : 6);
    const scaled = v * 10n ** BigInt(places);
    const rounded = scaled / ETH + ((scaled % ETH) * 2n >= ETH ? 1n : 0n);
    const s = rounded.toString().padStart(places + 1, '0');
    const whole = s.slice(0, s.length - places) || '0';
    const frac = places > 0 ? s.slice(s.length - places) : '';
    const grouped = Number(whole).toLocaleString('en-US');
    const out = frac ? `${grouped}.${frac}` : grouped;
    return `${neg ? '-' : ''}${out}${symbol ? ' ETH' : ''}`;
}
/** Compact ETH for tight cells: 1.24K, 18.6, 0.0042. */
export function compactEth(wei) {
    const n = Number(wei) / 1e18;
    if (n === 0)
        return '0';
    const a = Math.abs(n);
    if (a >= 1e6)
        return (n / 1e6).toFixed(2).replace(/\.?0+$/, '') + 'M';
    if (a >= 1e3)
        return (n / 1e3).toFixed(2).replace(/\.?0+$/, '') + 'K';
    if (a >= 1)
        return n.toFixed(2).replace(/\.?0+$/, '');
    if (a >= 1e-6)
        return n.toFixed(6).replace(/\.?0+$/, '');
    // Dust. Exponential notation is accurate and unreadable in a table cell, and
    // the exact wei is never what someone is asking when they glance at a column.
    return n < 0 ? '>-0.000001' : '<0.000001';
}
/** Whole-token counts, which run to nine figures here. */
export function formatTokens(amount, decimals = 18) {
    const n = Number(amount) / 10 ** decimals;
    if (n === 0)
        return '0';
    const a = Math.abs(n);
    if (a >= 1e9)
        return (n / 1e9).toFixed(2).replace(/\.?0+$/, '') + 'B';
    if (a >= 1e6)
        return (n / 1e6).toFixed(2).replace(/\.?0+$/, '') + 'M';
    if (a >= 1e3)
        return (n / 1e3).toFixed(1).replace(/\.?0+$/, '') + 'K';
    if (a >= 1)
        return n.toFixed(2).replace(/\.?0+$/, '');
    return n.toPrecision(3);
}
export function compactNumber(n) {
    const a = Math.abs(n);
    if (a >= 1e9)
        return (n / 1e9).toFixed(1).replace(/\.0$/, '') + 'B';
    if (a >= 1e6)
        return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
    if (a >= 1e3)
        return (n / 1e3).toFixed(a >= 1e4 ? 0 : 1).replace(/\.0$/, '') + 'K';
    return String(Math.round(n));
}
/** Parts per million as a percentage: 143210 -> "14.3%". */
export function formatPpm(ppm) {
    const n = Number(ppm) / 10_000;
    if (n === 0)
        return '0%';
    if (n < 0.1)
        return '<0.1%';
    return `${n.toFixed(n < 10 ? 1 : 0)}%`;
}
/** hh:mm:ss for the countdown; days appear once a period is badly overdue. */
export function formatDuration(seconds) {
    const s = Math.max(0, Math.floor(seconds));
    const d = Math.floor(s / 86400);
    const h = Math.floor(s / 3600) % 24;
    const m = Math.floor(s / 60) % 60;
    const sec = s % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return d > 0 ? `${d}d ${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(h)}:${pad(m)}:${pad(sec)}`;
}
export function shortAddress(a) {
    return a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a;
}
/** "4m ago", "2h ago". Ages here are usually minutes, so seconds matter. */
export function formatAge(unixSeconds, now = Date.now() / 1000) {
    const s = Math.max(0, Math.floor(now - unixSeconds));
    if (s < 60)
        return `${s}s ago`;
    if (s < 3600)
        return `${Math.floor(s / 60)}m ago`;
    if (s < 86400)
        return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
}
/** Parses a user-typed decimal ETH amount into wei without going through a float. */
export function parseEthInput(input) {
    const t = input.trim();
    if (!/^\d*(\.\d*)?$/.test(t) || t === '' || t === '.')
        return null;
    const [whole = '0', frac = ''] = t.split('.');
    if (frac.length > 18)
        return null;
    return BigInt(whole || '0') * ETH + BigInt((frac || '0').padEnd(18, '0'));
}
//# sourceMappingURL=format.js.map